import { FLAGS } from '@gozoki/feature-flags'
import { createFileRoute } from '@tanstack/react-router'
import { ChevronDown, Inbox, LayoutDashboard, Settings, Star, Users } from 'lucide-react'
import z from 'zod'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { AppTabs, type AppTab } from '~/components/new/app-ui/app-tabs'
import { Title } from '~/components/new/app-ui/title'
import { FeatureRouteGuard } from '~/components/route-guards'
import { InboxTab } from '~/components/routes/service-client/inbox-tab'
import { useInboxTickets } from '~/components/routes/service-client/service-client-api'
import { SERVICE_CLIENT_TICKET_READ } from '~/components/routes/service-client/service-client-rights'
import { Button } from '~/components/ui/button'
import { TabsContent } from '~/components/ui/tabs'
import { cn } from '~/lib/utils'
import { withRoutePermissions } from '~/utils/rbac'

type ServiceClientTab = 'dashboard' | 'inbox' | 'clients' | 'reviews'

export const Route = createFileRoute('/_authenticated/service-client/')({
  staticData: {
    permission: SERVICE_CLIENT_TICKET_READ,
  },
  component: ServiceClientRoute,
  validateSearch: z.object({
    tab: z.enum(['dashboard', 'inbox', 'clients', 'reviews']).optional(),
  }),
})

withRoutePermissions(Route)

/** Page Service client : flag `service-client-sidebar`, puis lecture des tickets. */
function ServiceClientRoute() {
  return (
    <FeatureRouteGuard flag={FLAGS.serviceClientSidebar}>
      <ServiceClientPage />
    </FeatureRouteGuard>
  )
}

// Compteur d'avis Google : mock tant que l'onglet dédié n'existe pas.
const GOOGLE_REVIEWS_COUNT = 7

/** Pastille compteur dans un onglet ; teinte selon que l'onglet est actif (fond vert) ou non. */
function TabCountBadge({ count, active }: { count: number; active: boolean }) {
  return (
    <span
      className={cn(
        'ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none text-primary-800',
        active ? 'bg-white' : 'bg-primary-50',
      )}
    >
      {count}
    </span>
  )
}

function ServiceClientPage() {
  const { tab } = Route.useSearch()
  const navigate = Route.useNavigate()

  // Même source que la liste (query partagée, mise en cache) : le badge d'onglet ne diverge pas du
  // compteur « N non lus » de la barre d'outils.
  const { tickets } = useInboxTickets()
  const unreadCount = tickets.filter((ticket) => ticket.unread).length

  const activeTab: ServiceClientTab = tab ?? 'inbox'
  const setTab = (next: ServiceClientTab) =>
    navigate({ search: (prev) => ({ ...prev, tab: next }) })

  const tabs: AppTab<ServiceClientTab>[] = [
    { value: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
    {
      value: 'inbox',
      label: (
        <span className="inline-flex items-center">
          Boîte de réception
          <TabCountBadge count={unreadCount} active={activeTab === 'inbox'} />
        </span>
      ),
      icon: Inbox,
    },
    { value: 'clients', label: 'Clients', icon: Users },
    {
      value: 'reviews',
      label: (
        <span className="inline-flex items-center">
          Avis Google
          <TabCountBadge count={GOOGLE_REVIEWS_COUNT} active={activeTab === 'reviews'} />
        </span>
      ),
      icon: Star,
    },
  ]

  return (
    <>
      <AppHeader>
        <Title>Service client</Title>
      </AppHeader>

      <AppContainer>
        <AppTabs
          tabs={tabs}
          value={activeTab}
          onTabChange={setTab}
          variant="pill"
          actions={<ServiceClientTopControls />}
        >
          <TabsContent value="dashboard">
            <ComingSoon />
          </TabsContent>
          <TabsContent value="inbox">
            <InboxTab />
          </TabsContent>
          <TabsContent value="clients">
            <ComingSoon />
          </TabsContent>
          <TabsContent value="reviews">
            <ComingSoon />
          </TabsContent>
        </AppTabs>
      </AppContainer>
    </>
  )
}

/**
 * Contrôles de la barre d'onglets (sélecteur de période, boutiques, réglages). Visuels
 * non fonctionnels pour ce ticket : câblage différé (cf. filtres de la Boîte de réception).
 */
function ServiceClientTopControls() {
  return (
    <>
      <Button variant="outline" disabled className="h-10 gap-4 px-4 text-primary-900">
        Sélectionner une période
        <ChevronDown />
      </Button>
      <Button variant="outline" disabled className="h-10 gap-2 px-4 text-primary-900">
        Toutes les boutiques
        <ChevronDown />
      </Button>
      <Button
        size="icon"
        disabled
        aria-label="Réglages"
        className="size-10 bg-primary-500 text-white"
      >
        <Settings className="size-5" />
      </Button>
    </>
  )
}

function ComingSoon() {
  return <p className="mt-6 text-sm text-muted-foreground">Bientôt disponible.</p>
}
